import { useFormik } from "formik";
import { useRouter } from "next/router";
import * as yup from 'yup';
import { Button, Input, PasswordInput } from "@netfront/ui-library";
import { IAcceptInvitationFormFields } from "./AcceptInvitationForm.interfaces";
import { saveAuthenticationData, useAcceptInvitation, useDomain, useLogout, usePublicGeladaLazyQuery } from "@netfront/gelada-identity-library";
import { ICookieOptionalAttributes, useIsMounted } from '@netfront/common-library';
import { useEffect, useState } from "react";
import { useToast } from "@netfront/ekardo-content-library";
import { gql } from '@apollo/client';

export const AcceptInvitationForm = () => {
  const { 
    push,
    query: { code },
  } = useRouter();
  const { isMounted } = useIsMounted();
  const { handleToastError } = useToast();
  const { getDomain, isDomainReady } = useDomain();
  const { handleLogout } = useLogout();
  
  const [email, setEmail] = useState<string>();

  const { handleAcceptInvitation, isLoading: isAcceptInvitationLoading = false } = useAcceptInvitation({
    onCompleted: ({ accessToken, refreshToken, user }) => {
      const domain = getDomain();
      const isSecure = process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE === 'true';
      const sameSite: ICookieOptionalAttributes['sameSite'] = isSecure ? 'None' : 'Lax';

      saveAuthenticationData({
        accessToken,
        accessTokenOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
        refreshToken,
        refreshTokenOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
        user,
        userOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
      });

      push('/dashboard').catch((error) =>
        handleToastError({
          shouldUseFriendlyErrorMessage: true,
          error,
        }),
      );
      
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const [getInvitation] = usePublicGeladaLazyQuery<{ getInvitation: { emailUserInvited: string } }, { token: string }>({
    options: {
      fetchPolicy: 'no-cache',
      onCompleted: ({ getInvitation: { emailUserInvited } }) => {
        setEmail(emailUserInvited);
      },
    },
    projectId: String(process.env.REACT_APP_PROJECT_ID),
    query: gql`
        query($token: String!) {
          getInvitation(token: $token) {
            emailUserInvited
          }
        }
      `,
  });

  useEffect(() => {
    if (!code) {
      return;
    }

    getInvitation({
      variables: {
        token: String(code),
      },
    });
  }, [code]);

  const formik = useFormik<IAcceptInvitationFormFields>({
    initialValues: {
      password: ''
    },
    validationSchema: yup.object().shape({
      password: yup.string().required('Please enter in a password'),
    }),
    onSubmit: async ({ password }: IAcceptInvitationFormFields) => {
      handleAcceptInvitation({
        projectId: String(process.env.REACT_APP_PROJECT_ID),
        request: {
          tokenId: String(code),
          password,
        },
      });
    }
  });

  useEffect(() => {
    if(!isDomainReady) return;

    handleLogout({ 
      domain: getDomain(), 
      path: '/' 
    });

  }, [isDomainReady])

  const { errors, touched, handleBlur, handleChange, handleSubmit } = formik;

  return isMounted ? (
    <form onSubmit={handleSubmit}>
      {email && (
        <Input
          id="email"
          labelText="Email"
          name="email"
          placeholder="Email"
          type="email"
          value={email}
          isReadOnly
          onChange={handleChange}
        />
      )}

      <PasswordInput
        errorMessage={touched.password && errors.password ? errors.password : ''}
        id='password'
        labelText='Password'
        name='password'
        placeholder='Enter password'
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <div className="flex flex-col lg:flex-row items-center pt-4 pb-8">
        <a href="/forgot-password" className="color-green-600 weight-600 ml-auto">Forgot password</a>
      </div>

      <Button
        additionalClassNames='w-full'
        variant='tertiary'
        text='Activate Account'
        type='submit'
        isLoading={isAcceptInvitationLoading}
      />
    </form>
  ) : null;
};